import { Localized } from "@fluent/react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, Select, Skeleton, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { LoadUsers } from "../../../app/AppDataLoader";
import { AppId } from "../../../app/appTypes";
import { CancelButton } from "../../../app/components/CancelButton";
import { SaveButton } from "../../../app/components/SaveButton";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectDocument } from "../documentSlice";
import { changeDocumentAuthor, loadDocumentTeamMembers, DocumentTeamMember } from "./teamMemberApi";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { selectLoggedUser } from "../../user/userSlice";
import { selectAllUsers, User } from "../../users/usersSlice";
import { selectModuleByCode } from "../../modules/moduleSlice";
import { changeAuthorDocumentTeamMember } from "../workflow/documentWorkflowApi";

export interface DocumentOwnerDialogProps {
    documentId: AppId;
    advancedWFL?: boolean;
    createdBy?: string;
    onSuccess: () => void;
    onCancel: () => void;
}

export const DocumentOwnerDialog = ({ documentId, advancedWFL, createdBy, onSuccess, onCancel }: DocumentOwnerDialogProps) => {
    const dispatch = useAppDispatch();
    const document = useAppSelector(selectDocument)?.doc?.header;
    const allUsers = useAppSelector(selectAllUsers);
    const loggedUser = useAppSelector(selectLoggedUser);
    const adminModule = useAppSelector(state => selectModuleByCode(state, "admin"));
    const [teamMembers, setTeamMembers] = useState<DocumentTeamMember[]>([]);
    const [loadingTeamMembers, setLoadingTeamMembers] = useState(true);
    const [selectedOwner, setSelectedOwner] = useState<User | null>(null);
    const [reason, setReason] = useState("");
    const [password, setPassword] = useState("");
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const documentAuthor = createdBy ?? document?.createdBy;
    const canChangeOwner = adminModule !== undefined || documentAuthor === loggedUser;
    const teamMemberUserNames = useMemo(
        () => new Set(teamMembers.map(teamMember => teamMember.userName)),
        [teamMembers]
    );
    const availableUsers = useMemo(
        () => allUsers.filter(user => !teamMemberUserNames.has(user.userName)),
        [allUsers, teamMemberUserNames]
    );

    useEffect(() => {
        let active = true;

        dispatch(loadDocumentTeamMembers(String(documentId)))
            .unwrap()
            .then(team => {
                if (active) {
                    setTeamMembers(team);
                }
            })
            .catch(() => {
                if (active) {
                    setServerError("error");
                }
            })
            .finally(() => {
                if (active) {
                    setLoadingTeamMembers(false);
                }
            });

        return () => {
            active = false;
        };
    }, [dispatch, documentId]);

    const handleSave = async () => {
        if (!canChangeOwner || !selectedOwner || !password.trim()) {
            return;
        }

        setSaving(true);
        setServerError("");
        try {
            if(advancedWFL) {
                const result = await dispatch(changeAuthorDocumentTeamMember({
                    documentId,
                    dto: {
                        userName: selectedOwner.userName,
                        reason,
                        password,
                    }
                }));

                if (changeAuthorDocumentTeamMember.fulfilled.match(result)) {
                    dispatch(showSuccess("saved"));
                    onSuccess();
                    window.location.reload();
                    return;
                }

                const problem = result.payload?.kind === "http" ? result.payload.problem : undefined;
                setServerError(problem?.title ?? "error");
            }
            const result = await dispatch(changeDocumentAuthor({
                documentId,
                userName: selectedOwner.userName,
                password,
            }));

            if (changeDocumentAuthor.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccess();
                window.location.reload();
                return;
            }

            const problem = result.payload?.kind === "http" ? result.payload.problem : undefined;
            setServerError(problem?.title ?? "error");
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onCancel}>
            <DialogTitle>
                <Localized id="document-action-change-owner">_Zmień właściciela dokumentu</Localized>
            </DialogTitle>
            <DialogContent>
                {!canChangeOwner && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <Localized id="document-action-change-owner-forbidden">_Nie masz uprawnień do wykonania tej akcji</Localized>
                    </Alert>
                )}
                <LoadUsers component={<Skeleton sx={{ mt: 2 }} />}>
                    <FormControl sx={{ mt: 2 }} fullWidth disabled={!canChangeOwner || loadingTeamMembers}>
                        <InputLabel id="document-owner-label">
                            <Localized id="document-document-company-owner">_Właściciel dokumentu</Localized>
                        </InputLabel>
                        <Select
                            labelId="document-owner-label"
                            value={selectedOwner?.userName ?? ""}
                            label={<Localized id="document-document-company-owner">_Właściciel dokumentu</Localized>}
                            onChange={event => {
                                setSelectedOwner(availableUsers.find(user => user.userName === event.target.value) ?? null);
                            }}
                        >
                            {availableUsers.map(user => (
                                <MenuItem key={user.userName} value={user.userName}>
                                    {user.firstname} {user.surname} ({user.email})
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{selectedOwner?.position ?? " "}</FormHelperText>
                    </FormControl>
                </LoadUsers>
                <TextField
                    sx={{ mt: 2 }}
                    rows={3}
                    fullWidth
                    required
                    value={reason}
                    disabled={!canChangeOwner}
                    onChange={event => setReason(event.target.value)}
                    multiline
                    label={<Localized id="document-action-change-owner-reason">_Powód</Localized>}
                />
                <TextField
                    sx={{ mt: 2 }}
                    fullWidth
                    required
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    disabled={!canChangeOwner}
                    onChange={event => setPassword(event.target.value)}
                    label={<Localized id="password">_Hasło</Localized>}
                />
                {serverError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setServerError("")}>
                        <Localized id={serverError}>_Wystąpił błąd serwera</Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} disabled={!canChangeOwner || loadingTeamMembers || !selectedOwner || !password.trim()} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};